// src/models/index.js

export class StateModel {
  constructor({ id, name }) {
    this.id = id;
    this.name = name;
  }

  static fromJson(json) {
    return new StateModel({
      id: json.id,
      name: json.name
    });
  }
}

export class Profession {
  constructor({ id, name, isStore, isDigitalProducts }) {
    this.id = id;
    this.name = name;
    this.isStore = isStore ?? false;
    this.isDigitalProducts = isDigitalProducts ?? false;
  }

  static fromJson(json) {
    return new Profession({
      id: json.id,
      name: json.name,
      isStore: json.is_store ?? false,
      isDigitalProducts: json.is_digital_products ?? false,
    });
  }
}

export class Provider {
  constructor({
    id,
    email,
    fullName,
    avatarUrl,
    stateId,
    address,
    professionId,
    isVerified,
    available,
    averageRating,
    phone,
    stateName,
    professionName,
    totalEarnings,
    netEarnings,
    providerId,
    cardNumber,
    isStore,
    isDigitalProducts,
    isFood,
    acceptsWallet,
    acceptsCash,
    wallet,          // ✅ جديد: الرصيد
    walletCode,      // ✅ جديد: كود المحفظة
    isActive,        // ✅ جديد: حالة الحساب (إيقاف)
    banReason,       // ✅ جديد: سبب الإيقاف
    createdAt
  }) {
    this.id = id;
    this.email = email;
    this.fullName = fullName;
    this.avatarUrl = avatarUrl;
    this.stateId = stateId;
    this.address = address;
    this.professionId = professionId;
    this.isVerified = isVerified;
    this.available = available;
    this.averageRating = averageRating;
    this.phone = phone;
    this.stateName = stateName;
    this.professionName = professionName;
    this.totalEarnings = totalEarnings;
    this.netEarnings = netEarnings;
    this.providerId = providerId;
    this.cardNumber = cardNumber;
    this.isStore = isStore ?? false;
    this.isDigitalProducts = isDigitalProducts ?? false;
    this.isFood = isFood ?? false;
    this.acceptsWallet = acceptsWallet ?? false;
    this.acceptsCash = acceptsCash ?? true;
    this.wallet = wallet ?? 0;             // ✅ تعيين القيمة
    this.walletCode = walletCode;          // ✅ تعيين القيمة
    this.isActive = isActive ?? true;      // ✅ تعيين القيمة
    this.banReason = banReason || null;    // ✅ تعيين القيمة
    this.createdAt = createdAt;
  }

  static fromJson(json) {
    return new Provider({
      id: json.id,
      email: json.email || '',
      fullName: json.full_name,
      avatarUrl: json.avatar_url,
      stateId: json.state_id,
      address: json.address,
      professionId: json.profession_id,
      isVerified: json.is_verified,
      available: json.available,
      averageRating: (json.average_rating) ? parseFloat(json.average_rating) : 0,
      phone: json.phone,
      stateName: json.states?.name || json.state_name,
      professionName: json.professions?.name || json.profession_name,
      totalEarnings: (json.total_earnings) ? parseFloat(json.total_earnings) : 0,
      netEarnings: (json.net_earnings) ? parseFloat(json.net_earnings) : 0,
      providerId: json.provider_id || '000000000',
      cardNumber: json.card_number || '0000000000000000',
      isStore: json.is_store ?? json.professions?.is_store ?? false,
      isDigitalProducts: json.is_digital_products ?? json.professions?.is_digital_products ?? false,
      isFood: json.is_food ?? json.professions?.is_food ?? false,
      acceptsWallet: json.accepts_wallet ?? false,
      acceptsCash: json.accepts_cash ?? true,
      wallet: json.wallet ?? 0,                      // ✅ استخراج من الـ JSON
      walletCode: json.wallet_code || null,          // ✅ استخراج من الـ JSON
      isActive: json.is_active ?? true,              // ✅ استخراج من الـ JSON
      banReason: json.ban_reason || null,            // ✅ استخراج من الـ JSON
      createdAt: json.created_at ? new Date(json.created_at) : null,
    });
  }

  toJson() {
    return {
      id: this.id,
      email: this.email,
      full_name: this.fullName,
      avatar_url: this.avatarUrl,
      state_id: this.stateId,
      address: this.address,
      profession_id: this.professionId,
      is_verified: this.isVerified,
      available: this.available,
      phone: this.phone,
      provider_id: this.providerId,
      card_number: this.cardNumber,
      // ❌ تمت إزالة is_store و is_digital_products
      // هذان الحقلان غير موجودين كأعمدة في جدول providers أصلاً؛
      // هما أعمدة في جدول professions ويتم دمجهما في الكائن فقط عند القراءة (join) للعرض،
      // وليس للتخزين المباشر في providers.
      accepts_wallet: this.acceptsWallet,
      accepts_cash: this.acceptsCash,
      wallet: this.wallet, // إضافة للتوفر
      is_active: this.isActive,
      ban_reason: this.banReason,
    };
  }

  copyWith({
    id, email, fullName, avatarUrl, stateId, address, professionId, isVerified, available, averageRating, phone, stateName, professionName, totalEarnings, netEarnings, providerId, cardNumber, isStore, isDigitalProducts, isFood, acceptsWallet, acceptsCash, wallet, walletCode, isActive, banReason, createdAt
  }) {
    return new Provider({
      id: id ?? this.id,
      email: email ?? this.email,
      fullName: fullName ?? this.fullName,
      avatarUrl: avatarUrl ?? this.avatarUrl,
      stateId: stateId ?? this.stateId,
      address: address ?? this.address,
      professionId: professionId ?? this.professionId,
      isVerified: isVerified ?? this.isVerified,
      available: available ?? this.available,
      averageRating: averageRating ?? this.averageRating,
      phone: phone ?? this.phone,
      stateName: stateName ?? this.stateName,
      professionName: professionName ?? this.professionName,
      totalEarnings: totalEarnings ?? this.totalEarnings,
      netEarnings: netEarnings ?? this.netEarnings,
      providerId: providerId ?? this.providerId,
      cardNumber: cardNumber ?? this.cardNumber,
      isStore: isStore ?? this.isStore,
      isDigitalProducts: isDigitalProducts ?? this.isDigitalProducts,
      isFood: isFood ?? this.isFood,
      acceptsWallet: acceptsWallet ?? this.acceptsWallet,
      acceptsCash: acceptsCash ?? this.acceptsCash,
      wallet: wallet ?? this.wallet,
      walletCode: walletCode ?? this.walletCode,
      isActive: isActive ?? this.isActive,
      banReason: banReason ?? this.banReason,
      createdAt: createdAt ?? this.createdAt,
    });
  }
}

export class Client {
  constructor({
    id,
    email,
    fullName,
    avatarUrl,
    stateId,
    address,
    phone,
    clientId,
    cardNumber,
    wallet,          // ✅ جديد
    walletFrozen,    // ✅ جديد
    walletCode,      // ✅ جديد
    isActive,        // ✅ جديد: حالة الحساب (إيقاف)
    banReason,       // ✅ جديد: سبب الإيقاف
    createdAt,
    updatedAt
  }) {
    this.id = id;
    this.email = email;
    this.fullName = fullName;
    this.avatarUrl = avatarUrl;
    this.stateId = stateId;
    this.address = address;
    this.phone = phone;
    this.clientId = clientId;
    this.cardNumber = cardNumber;
    this.wallet = wallet ?? 0;             // ✅ تعيين القيمة
    this.walletFrozen = walletFrozen ?? 0; // ✅ تعيين القيمة
    this.walletCode = walletCode;          // ✅ تعيين القيمة
    this.isActive = isActive ?? true;      // ✅ تعيين القيمة
    this.banReason = banReason || null;    // ✅ تعيين القيمة
    this.createdAt = createdAt;
    this.updatedAt = updatedAt;
    
    // حساب الرصيد المتاح تلقائياً
    this.availableBalance = this.wallet - this.walletFrozen;
  }

  static fromJson(json) {
    return new Client({
      id: json.id,
      email: json.email,
      fullName: json.full_name || json.fullName || '',
      avatarUrl: json.avatar_url,
      stateId: json.state_id,
      address: json.address,
      phone: json.phone,
      clientId: json.client_id || '000000000',
      cardNumber: json.card_number || '0000000000000000',
      wallet: json.wallet ?? 0,                     // ✅ استخراج من الـ JSON
      walletFrozen: json.wallet_frozen ?? 0,        // ✅ استخراج من الـ JSON
      walletCode: json.wallet_code || null,         // ✅ استخراج من الـ JSON
      isActive: json.is_active ?? true,             // ✅ استخراج من الـ JSON
      banReason: json.ban_reason || null,           // ✅ استخراج من الـ JSON
      createdAt: json.created_at ? new Date(json.created_at) : null,
      updatedAt: json.updated_at ? new Date(json.updated_at) : null,
    });
  }

  toJson() {
    return {
      email: this.email,
      full_name: this.fullName,
      avatar_url: this.avatarUrl,
      state_id: this.stateId,
      address: this.address,
      phone: this.phone,
      client_id: this.clientId,
      card_number: this.cardNumber,
      wallet: this.wallet,
      wallet_frozen: this.walletFrozen,
      wallet_code: this.walletCode,
      is_active: this.isActive,
      ban_reason: this.banReason,
    };
  }

  copyWith({
    id, email, fullName, avatarUrl, stateId, address, phone, clientId, cardNumber, wallet, walletFrozen, walletCode, isActive, banReason, createdAt, updatedAt
  }) {
    return new Client({
      id: id ?? this.id,
      email: email ?? this.email,
      fullName: fullName ?? this.fullName,
      avatarUrl: avatarUrl ?? this.avatarUrl,
      stateId: stateId ?? this.stateId,
      address: address ?? this.address,
      phone: phone ?? this.phone,
      clientId: clientId ?? this.clientId,
      cardNumber: cardNumber ?? this.cardNumber,
      wallet: wallet ?? this.wallet,
      walletFrozen: walletFrozen ?? this.walletFrozen,
      walletCode: walletCode ?? this.walletCode,
      isActive: isActive ?? this.isActive,
      banReason: banReason ?? this.banReason,
      createdAt: createdAt ?? this.createdAt,
      updatedAt: updatedAt ?? this.updatedAt,
    });
  }
}

export class Service {
  constructor({
    id, providerId, name, description, price, imageUrl, stock, categoryId, categoryName, averageRating, isActive, productType, fileUrl, previewUrl, previewImages, mediaUrls, qrData, allowDownload,
  }) {
    this.id = id;
    this.providerId = providerId;
    this.name = name;
    this.description = description;
    this.price = price;
    this.imageUrl = imageUrl;
    this.stock = stock;
    this.categoryId = categoryId;
    this.categoryName = categoryName;
    this.averageRating = averageRating;
    this.isActive = isActive;
    this.productType = productType;
    this.fileUrl = fileUrl;
    this.previewUrl = previewUrl;
    this.previewImages = previewImages || [];
    this.mediaUrls = mediaUrls || [];
    this.qrData = qrData;
    // التوافق مع المنتجات القديمة: null/undefined يُفسّر كسماح بالتحميل افتراضياً
    this.allowDownload = allowDownload !== false;
  }

  static fromJson(json) {
    return new Service({
      id: json.id, providerId: json.provider_id, name: json.name, description: json.description,
      price: (json.price) ? parseFloat(json.price) : 0, imageUrl: json.image_url, stock: json.stock,
      categoryId: json.category_id, categoryName: json.category_name || json.categories?.name,
      averageRating: (json.average_rating) ? parseFloat(json.average_rating) : 0, isActive: json.is_active,
      productType: json.product_type, fileUrl: json.file_url, previewUrl: json.preview_url, previewImages: json.preview_images || [],
      mediaUrls: json.media_urls || [],
      qrData: json.qr_data,
      allowDownload: json.allow_download !== false,
    });
  }

  toJson() {
    return {
      id: this.id, provider_id: this.providerId, name: this.name, description: this.description, price: this.price,
      image_url: this.imageUrl, stock: this.stock, category_id: this.categoryId, is_active: this.isActive,
      product_type: this.productType, file_url: this.fileUrl, preview_url: this.previewUrl, preview_images: this.previewImages,
      media_urls: this.mediaUrls,
      qr_data: this.qrData,
      allow_download: this.allowDownload,
    };
  }
}

export class Request {
  constructor({ id, clientId, providerId, serviceId, status, service, provider, client, rating, review, paymentMethod, quantity, createdAt, updatedAt }) {
    this.id = id; this.clientId = clientId; this.providerId = providerId; this.serviceId = serviceId; this.status = status;
    this.service = service; this.provider = provider; this.client = client; this.rating = rating; this.review = review;
    this.paymentMethod = paymentMethod; this.quantity = quantity ?? 1; this.createdAt = createdAt; this.updatedAt = updatedAt;
  }

  static fromJson(json) {
    return new Request({
      id: json.id, clientId: json.client_id, providerId: json.provider_id, serviceId: json.service_id, status: json.status,
      service: json.services ? Service.fromJson(json.services) : null,
      provider: json.providers ? Provider.fromJson(json.providers) : null,
      client: json.clients ? Client.fromJson(json.clients) : null,
      rating: json.rating, review: json.review, paymentMethod: json.payment_method, quantity: json.quantity ?? 1,
      createdAt: json.created_at ? new Date(json.created_at) : null, updatedAt: json.updated_at ? new Date(json.updated_at) : null,
    });
  }
  toJson() { return { client_id: this.clientId, provider_id: this.providerId, service_id: this.serviceId, status: this.status, rating: this.rating, review: this.review, payment_method: this.paymentMethod, quantity: this.quantity }; }
}

export class Conversation {
  constructor({ id, clientId, providerId, client, provider, createdAt, lastMessage }) {
    this.id = id; this.clientId = clientId; this.providerId = providerId; this.client = client; this.provider = provider; this.createdAt = createdAt; this.lastMessage = lastMessage;
  }
  static fromJson(json) {
    return new Conversation({
      id: json.id, clientId: json.client_id, providerId: json.provider_id,
      client: json.clients ? Client.fromJson(json.clients) : null, provider: json.providers ? Provider.fromJson(json.providers) : null,
      createdAt: json.created_at ? new Date(json.created_at) : null, lastMessage: json.last_message ? Message.fromJson(json.last_message) : null,
    });
  }
}

export class Message {
  constructor({ id, conversationId, senderId, content, createdAt, replyToId, replyToMessage }) {
    this.id = id; this.conversationId = conversationId; this.senderId = senderId; this.content = content; this.createdAt = createdAt; this.replyToId = replyToId; this.replyToMessage = replyToMessage;
  }
  static fromJson(json) {
    if (!json) return new Message({});
    return new Message({
      id: json.id, conversationId: json.conversation_id, senderId: json.sender_id, content: json.content || '',
      createdAt: json.created_at ? new Date(json.created_at) : new Date(), replyToId: json.reply_to_id,
      replyToMessage: json.reply_to_message ? Message.fromJson(json.reply_to_message) : null,
    });
  }
  toJson() { const d = { conversation_id: this.conversationId, sender_id: this.senderId, content: this.content, reply_to_id: this.replyToId }; if (this.id) d.id = this.id; return d; }
}

export class AppNotification {
  constructor({ id, userId, title, message, isRead, createdAt, updatedAt }) {
    this.id = id; this.userId = userId; this.title = title; this.message = message; this.isRead = isRead; this.createdAt = createdAt; this.updatedAt = updatedAt;
  }
  static fromJson(json) {
    return new AppNotification({
      id: json.id, userId: json.user_id, title: json.title, message: json.message, isRead: json.is_read,
      createdAt: json.created_at ? new Date(json.created_at) : null, updatedAt: json.updated_at ? new Date(json.updated_at) : null,
    });
  }
  toJson() { return { user_id: this.userId, title: this.title, message: this.message, is_read: this.isRead }; }
}

export class MonthlyPayment {
  constructor({ id, providerId, month, totalEarnings, commission, netEarnings, paymentStatus, receiptUrl, createdAt, updatedAt }) {
    this.id = id; this.providerId = providerId; this.month = month; this.totalEarnings = totalEarnings; this.commission = commission; this.netEarnings = netEarnings;
    this.paymentStatus = paymentStatus; this.receiptUrl = receiptUrl; this.createdAt = createdAt; this.updatedAt = updatedAt;
  }
  static fromJson(json) {
    return new MonthlyPayment({
      id: json.id, providerId: json.provider_id, month: json.month ? new Date(json.month) : null,
      totalEarnings: (json.total_earnings) ? parseFloat(json.total_earnings) : 0,
      commission: (json.commission) ? parseFloat(json.commission) : 0,
      netEarnings: (json.net_earnings) ? parseFloat(json.net_earnings) : 0,
      paymentStatus: json.payment_status, receiptUrl: json.receipt_url,
      createdAt: json.created_at ? new Date(json.created_at) : null, updatedAt: json.updated_at ? new Date(json.updated_at) : null,
    });
  }
  toJson() { return { id: this.id, provider_id: this.providerId, month: this.month ? this.month.toISOString() : null, total_earnings: this.totalEarnings, commission: this.commission, net_earnings: this.netEarnings, payment_status: this.paymentStatus, receipt_url: this.receiptUrl }; }
}

export class Ad {
  constructor({ id, title, description, mediaUrl, mediaType, link }) {
    this.id = id; this.title = title; this.description = description; this.mediaUrl = mediaUrl; this.mediaType = mediaType; this.link = link;
  }
  static fromJson(json) { return new Ad({ id: json.id, title: json.title, description: json.description, mediaUrl: json.media_url, mediaType: json.media_type, link: json.link }); }
  toJson() { return { id: this.id, title: this.title, description: this.description, media_url: this.mediaUrl, media_type: this.mediaType, link: this.link }; }
}